import React, { useState, useEffect } from 'react'
import { X, Search, RotateCcw, Loader2, Package, ShoppingCart } from 'lucide-react'
import { Transaction, Product } from '@/types/pos'
import { apiService } from '@/services/api'

interface ReturnItemModalProps {
  onAddReturn: (item: {
    product: Product
    quantity: number
    unitPrice: number
    transactionId: string
    receiptNumber: string
    batchId?: string
    batchLabel?: string
  }) => void
  onClose: () => void
}

export const ReturnItemModal: React.FC<ReturnItemModalProps> = ({
  onAddReturn,
  onClose,
}) => {
  const [searchTerm, setSearchTerm] = useState('')
  const [transactions, setTransactions] = useState<Transaction[]>([])
  const [selectedTransaction, setSelectedTransaction] = useState<Transaction | null>(null)
  const [selectedItems, setSelectedItems] = useState<Map<string, { quantity: number; maxQty: number; item: any }>>(new Map())
  const [isLoading, setIsLoading] = useState(false)
  const [step, setStep] = useState<'search' | 'select-items'>('search')

  // Fetch recent completed transactions
  useEffect(() => {
    const fetchTransactions = async () => {
      setIsLoading(true)
      try {
        const response = await apiService.get<{ success: boolean; data: Transaction[] }>('/transactions')
        if (response.success) {
          const completed = response.data.filter(t => t.status === 'completed')
          setTransactions(completed)
        }
      } catch (error) {
        console.error('Failed to fetch transactions:', error)
      } finally {
        setIsLoading(false)
      }
    }
    fetchTransactions()
  }, [])

  const filteredTransactions = transactions.filter(t => {
    const term = searchTerm.toLowerCase()
    const transNum = (t.transactionNumber || t.receiptNumber || t.id || '').toLowerCase()
    const custName = ((t as any).customerName || '').toLowerCase()
    return transNum.includes(term) || custName.includes(term)
  })

  const handleSelectTransaction = (transaction: Transaction) => {
    setSelectedTransaction(transaction)
    setSelectedItems(new Map())
    setStep('select-items')
  }

  const toggleItem = (item: any, index: number, checked: boolean) => {
    const newSelected = new Map(selectedItems)
    const key = item.batchId
      ? `${item.productId || item.product?.id}-${item.batchId}-${index}`
      : `${item.productId || item.product?.id || index}-${index}`

    if (checked) {
      newSelected.set(key, {
        quantity: item.quantity,
        maxQty: item.quantity,
        item,
      })
    } else {
      newSelected.delete(key)
    }
    setSelectedItems(newSelected)
  }

  const updateQuantity = (key: string, quantity: number) => {
    const newSelected = new Map(selectedItems)
    const entry = newSelected.get(key)
    if (entry && quantity > 0 && quantity <= entry.maxQty) {
      newSelected.set(key, { ...entry, quantity })
      setSelectedItems(newSelected)
    }
  }

  const getItemKey = (item: any, index: number) => {
    return item.batchId
      ? `${item.productId || item.product?.id}-${item.batchId}-${index}`
      : `${item.productId || item.product?.id || index}-${index}`
  }

  const handleAddReturns = () => {
    if (!selectedTransaction) return

    selectedItems.forEach((entry) => {
      const item = entry.item
      const unitPrice = item.total && item.quantity
        ? item.total / item.quantity
        : (item.unitPrice || item.price || item.product?.price || 0)

      // Build a minimal product object for the cart
      const product: Product = {
        id: item.productId || item.product?.id || '',
        _id: item.productId || item.product?.id || '',
        name: item.productName || item.product?.name || item.name || 'Unknown',
        price: unitPrice,
        sku: item.sku || item.product?.sku || '',
        stock: 0,
        category: { id: '', name: '' } as any,
        isActive: true,
        createdAt: new Date(),
        updatedAt: new Date(),
        cost: 0,
        minStock: 0,
        maxStock: 0,
        unit: '',
        tags: [],
      }

      onAddReturn({
        product,
        quantity: entry.quantity,
        unitPrice,
        transactionId: selectedTransaction.id,
        receiptNumber: selectedTransaction.receiptNumber || selectedTransaction.transactionNumber || selectedTransaction.id,
        batchId: item.batchId || undefined,
        batchLabel: item.batchLabel || undefined,
      })
    })

    onClose()
  }

  const calculateReturnTotal = () => {
    let total = 0
    selectedItems.forEach((entry) => {
      const item = entry.item
      const unitPrice = item.total && item.quantity
        ? item.total / item.quantity
        : (item.unitPrice || item.price || item.product?.price || 0)
      total += unitPrice * entry.quantity
    })
    return total
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="glass-card max-w-2xl w-full max-h-[85vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex justify-between items-center mb-6 flex-shrink-0">
          <div>
            <h3 className="text-xl font-bold esales-text flex items-center gap-2">
              <RotateCcw className="w-5 h-5" />
              Return Item to Cart
            </h3>
            <p className="text-xs text-muted-foreground mt-1">
              {step === 'search' && 'Search the original transaction to return items from'}
              {step === 'select-items' && 'Select items to add as return credits to the current cart'}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-muted transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto">
          {/* Step 1: Search Transaction */}
          {step === 'search' && (
            <div className="space-y-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Search by receipt/transaction number or customer..."
                  className="w-full pl-10 pr-4 py-3 bg-background/50 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:outline-none"
                  autoFocus
                />
              </div>

              {isLoading ? (
                <div className="py-12 text-center">
                  <Loader2 className="w-8 h-8 animate-spin text-primary mx-auto mb-2" />
                  <p className="text-sm text-muted-foreground">Loading transactions...</p>
                </div>
              ) : (
                <div className="space-y-2 max-h-[400px] overflow-y-auto">
                  {filteredTransactions.length === 0 ? (
                    <div className="py-12 text-center text-muted-foreground">
                      <p>No transactions found</p>
                    </div>
                  ) : (
                    filteredTransactions.slice(0, 20).map((transaction) => (
                      <div
                        key={transaction.id}
                        onClick={() => handleSelectTransaction(transaction)}
                        className="p-4 rounded-xl border border-border/50 hover:border-primary/50 cursor-pointer transition-all bg-muted/10 hover:bg-muted/20"
                      >
                        <div className="flex justify-between items-start">
                          <div>
                            <p className="font-bold text-sm">
                              #{transaction.transactionNumber || transaction.receiptNumber || transaction.id}
                            </p>
                            <p className="text-xs text-muted-foreground mt-1">
                              {new Date(transaction.createdAt).toLocaleString()}
                            </p>
                            {(transaction as any).customerName && (transaction as any).customerName !== 'Guest' && (
                              <p className="text-xs text-muted-foreground">
                                Customer: {(transaction as any).customerName}
                              </p>
                            )}
                          </div>
                          <div className="text-right">
                            <p className="font-bold text-primary">Rs.{transaction.total.toFixed(2)}</p>
                            <p className="text-xs text-muted-foreground">
                              {(transaction.items as any[]).length} item(s)
                            </p>
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              )}
            </div>
          )}

          {/* Step 2: Select Items to Return */}
          {step === 'select-items' && selectedTransaction && (
            <div className="space-y-4">
              <div className="p-3 bg-muted/20 rounded-lg">
                <p className="text-xs text-muted-foreground">Returning from transaction</p>
                <p className="font-bold">
                  #{selectedTransaction.transactionNumber || selectedTransaction.receiptNumber}
                </p>
                <p className="text-xs text-muted-foreground">
                  {new Date(selectedTransaction.createdAt).toLocaleString()} — Total: Rs.{selectedTransaction.total.toFixed(2)}
                </p>
              </div>

              <div className="space-y-2">
                <p className="text-sm font-semibold">Select items to return:</p>
                {(selectedTransaction.items as any[]).map((item, index) => {
                  const key = getItemKey(item, index)
                  const isSelected = selectedItems.has(key)
                  const selectedEntry = selectedItems.get(key)
                  const itemTotal = item.total || item.unitPrice * item.quantity || item.product?.price * item.quantity || 0
                  const unitPrice = item.quantity > 0 ? itemTotal / item.quantity : 0

                  return (
                    <div
                      key={`${index}-${item.batchId || 'default'}`}
                      className={`p-3 rounded-lg border-2 transition-all ${
                        isSelected ? 'border-orange-500/60 bg-orange-500/5' : 'border-border/50 bg-muted/10'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={(e) => toggleItem(item, index, e.target.checked)}
                          className="w-4 h-4 rounded border-border accent-orange-500"
                        />
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <p className="font-semibold text-sm">
                              {item.productName || item.product?.name || item.name}
                            </p>
                            {item.batchId && (
                              <span className="text-[9px] font-bold uppercase px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-400 leading-none">
                                Batch
                              </span>
                            )}
                          </div>
                          <p className="text-xs text-muted-foreground">
                            Rs.{unitPrice.toFixed(2)} x {item.quantity} = Rs.{itemTotal.toFixed(2)}
                          </p>
                        </div>
                        {isSelected && (
                          <div className="flex items-center gap-2">
                            <span className="text-xs text-muted-foreground">Qty:</span>
                            <input
                              type="number"
                              min={1}
                              max={item.quantity}
                              value={selectedEntry?.quantity || item.quantity}
                              onChange={(e) => updateQuantity(key, parseInt(e.target.value) || 1)}
                              className="w-16 px-2 py-1 text-sm bg-background border border-border rounded text-center"
                            />
                          </div>
                        )}
                      </div>
                    </div>
                  )
                })}
              </div>

              {selectedItems.size > 0 && (
                <div className="p-4 bg-orange-500/10 border border-orange-500/30 rounded-xl">
                  <div className="flex justify-between items-center">
                    <div className="flex items-center gap-2">
                      <ShoppingCart className="w-4 h-4 text-orange-400" />
                      <span className="font-semibold text-orange-400">Return Credit:</span>
                    </div>
                    <span className="text-2xl font-bold text-orange-400">-Rs.{calculateReturnTotal().toFixed(2)}</span>
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">
                    This amount will be deducted from the current bill
                  </p>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex-shrink-0 mt-4 pt-4 border-t border-border/50 flex gap-3">
          {step === 'search' && (
            <button
              onClick={onClose}
              className="w-full py-3 bg-muted hover:bg-muted/80 rounded-xl font-semibold text-sm transition-colors"
            >
              Cancel
            </button>
          )}

          {step === 'select-items' && (
            <>
              <button
                onClick={() => { setStep('search'); setSelectedTransaction(null); setSelectedItems(new Map()) }}
                className="flex-1 py-3 bg-muted hover:bg-muted/80 rounded-xl font-semibold text-sm transition-colors"
              >
                Back
              </button>
              <button
                onClick={handleAddReturns}
                disabled={selectedItems.size === 0}
                className="flex-1 py-3 bg-orange-500 text-white rounded-xl font-semibold text-sm disabled:opacity-50 flex items-center justify-center gap-2 transition-colors hover:bg-orange-600"
              >
                <Package className="w-4 h-4" />
                Add {selectedItems.size} Return{selectedItems.size !== 1 ? 's' : ''} to Cart
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  )
}

export default ReturnItemModal
